import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Patientv2 } from '../../types';

// Define the shape of the context data
interface PatientContextType {
  patientId: number;
  addSubPatient: (id: number) => void;
  removeSubPatient: () => void;
  getSubPatient: () => number;
  
  subPatientData?: Patientv2[];
  setSubPatientData?: (data: Patientv2[]) => void;
}

// Define the props for the provider component
interface PatientProviderProps {
  children: ReactNode;
}

// Create the context
const PatientContext = createContext<PatientContextType | undefined>(undefined);

// Create a provider component
export const SubPatientProvider: React.FC<PatientProviderProps> = ({ children }) => {
  const [patientId, setPatientId] = useState<number>(-1); // Default value indicating no patient
  const [subPatientData, setSubPatientData] = useState<Patientv2[] | undefined>(undefined);

  // Add a patient by setting the patientId
  const addSubPatient = useCallback((id: number): void => {
    if (id > 0) { // Ensure the patient ID is valid (positive integer)
      setPatientId(id);
    } else {
      // eslint-disable-next-line no-console, quotes
      console.error("Invalid patient ID. Must be a positive number.");
    }
  }, []);

  // Remove the patient by resetting the patientId to its default state
  const removeSubPatient = useCallback((): void => {
    setPatientId(-1); // Reset to default value
  }, []);

  // Get the current patientId
  const getSubPatient = useCallback((): number => {
    return patientId;
  }, [patientId]);

  // Prepare context value, only include subPatientData and setSubPatientData if defined
  const contextValue: PatientContextType = {
    patientId,
    addSubPatient,
    removeSubPatient,
    getSubPatient,
    ...(typeof subPatientData !== 'undefined' ? { subPatientData } : {}),
    ...(typeof setSubPatientData !== 'undefined' ? { setSubPatientData } : {}),
  };

  return (
    <PatientContext.Provider value={contextValue}>
      {children}
    </PatientContext.Provider>
  );
};

// Custom hook to use the PatientContext
export const usePatientContext = (): PatientContextType => {
  const context = useContext(PatientContext);
  if (!context) {
    throw new Error('usePatientContext must be used within a PatientProvider');
  }
  return context;
};
